import { createNavigationContainerRef, CommonActions } from '@react-navigation/native';
import { getTabIdForScreen } from 'app/navigation/routeRegistry';
import { keysToCamelCase } from 'app/shared/utils/stringUtils';
import { Telemetry } from './telemetry/Telemetry';

export const navigationRef = createNavigationContainerRef();

const MAIN_STACK_NAME = 'Main';

export const handleNotificationNavigation = async (notificationData: any) => {
  const { type } = notificationData;

  Telemetry.event('nav.notification.received', { domain: 'nav', type, payload: JSON.stringify(notificationData) }, 'info');

  try {
    switch (type) {
      case 'report':
        await handleReportNavigation(notificationData);
        break;
      case 'chat':
        await handleChatNavigation(notificationData);
        break;
      case 'page_navigation':
        await handlePageNavigation(notificationData);
        break;
      case 'remeasurement':
        await handleRemeasurementNavigation(notificationData);
        break;
      default:
        Telemetry.event('nav.notification.unhandled_type', { domain:'nav', type }, 'warning');
    }
  } catch (error) {
    Telemetry.error(error, { event:'nav.notification.handle.fail', domain:'nav', type });
  }
};

const handleReportNavigation = async (data: any) => {
  const { reportId } = data;
  if (!reportId) {
    Telemetry.event('nav.deeplink.missing_param', { domain:'nav', param:'reportId' }, 'error');
    return;
  }

  try {
    const { api } = await import('app/api/client');
    const downloadUrl = `/patient/api/v1/telemonitoring-reports/${reportId}/download`;
    const response = await api.get<{ signed_url: string }>(downloadUrl);

    if (navigationRef.isReady()) {
      (navigationRef as any).navigate('PdfDocumentPage', {
        pdfUrl: response.signed_url,
      });
    }
  } catch (error) {
    Telemetry.error(error, { event:'nav.report.download.fail', domain:'nav', reportId });
  }
};

const handleChatNavigation = async (data: any) => {
  const { channelUrl } = data;
  if (!channelUrl) {
    Telemetry.event('nav.deeplink.missing_param', { domain:'nav', param:'channelUrl' }, 'error');
    return;
  }
  navigateToPage('Chat', { channelUrl });
};

const handlePageNavigation = async (data: any) => {
  const { page, params } = data;
  if (!page) {
    Telemetry.event('nav.deeplink.missing_param', { domain:'nav', param:'page' }, 'error');
    return;
  }

  let parsedParams = params;
  if (typeof params === 'string') {
    try {
      parsedParams = JSON.parse(params);
    } catch (e) {
      Telemetry.error(e, { event:'nav.deeplink.params.parse_fail', domain:'nav' });
      parsedParams = {};
    }
  }

  const camelParams = keysToCamelCase(parsedParams);

  const finalParams = {
    ...camelParams,
    measurementId: camelParams.measurementId || camelParams.measId,
    vitalParameterName: camelParams.vitPName || camelParams.vitalParameterName,
  };

  navigateToPage(page, finalParams);
};

const handleRemeasurementNavigation = async (data: any) => {
  const { measurementId, vitalParameterName } = data;
  if (!measurementId) {
    Telemetry.event('nav.deeplink.missing_param', { domain: 'nav', param: 'measurementId' }, 'error');
    return;
  }
  navigateToPage('RemeasurementStart', { measurementId, vitalParameterName });
};

const navigateToPage = (page: string, params: any) => {
  if (!navigationRef.isReady()) return;

  try {
    const tabId = getTabIdForScreen(page);
    if (tabId != null) {
      navigationRef.dispatch(
        CommonActions.navigate({
          name: MAIN_STACK_NAME,
          params: {
            screen: tabId,
            params: {
              screen: page,
              params,
            },
          },
        })
      );
    } else {
      (navigationRef as any).navigate(page, params);
    }
  } catch (error) {
    Telemetry.error(error, { event: 'nav.navigate.fail', domain: 'nav', target: page });
  }
};
